import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Upload, Trash2, AlertCircle, CheckCircle2, Loader2, FileSpreadsheet, Trophy, Medal } from "lucide-react";

/**
 * ResultsImportModal
 *   Props: championshipId, championshipTitle, onClose, onUpdated
 * Admin uploads a results file (xlsx/csv/pdf/docx/image). For spreadsheets we
 * parse inline; for PDF/DOCX/images we queue a background job and poll.
 */
export default function ResultsImportModal({ championshipId, championshipTitle, onClose, onUpdated }) {
  const [existing, setExisting] = useState({ total: 0, grouped: [] });
  const [busy, setBusy] = useState(false);
  const [replace, setReplace] = useState(true);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [status, setStatus] = useState(null); // {status, parsed, inserted, error, source_file}
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await api.get(`/championships/${championshipId}/results`);
      setExisting(r.data || { total: 0, grouped: [] });
    } catch { setExisting({ total: 0, grouped: [] }); }
  };

  useEffect(() => { refresh(); return () => clearInterval(pollRef.current); }, [championshipId]);

  const pollJob = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/results-jobs/${jobId}`);
        setStatus(r.data);
        if (r.data.status === "done" || r.data.status === "failed") {
          clearInterval(pollRef.current);
          setBusy(false);
          if (r.data.status === "done") {
            setOk(`Parsed ${r.data.parsed} rows · Inserted ${r.data.inserted}.`);
            await refresh();
            onUpdated && onUpdated();
          } else {
            setErr(r.data.error || "Parsing failed.");
          }
        }
      } catch (e) { /* keep polling */ }
    }, 4000);
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(""); setOk(""); setStatus({ status: "uploading", source_file: f.name }); setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(
        `/championships/${championshipId}/results/import?replace=${replace ? "true" : "false"}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }
      );
      if (r.data.mode === "sync") {
        setOk(`Parsed ${r.data.parsed} rows · Inserted ${r.data.inserted}.`);
        setStatus({ status: "done", parsed: r.data.parsed, inserted: r.data.inserted, source_file: f.name });
        setBusy(false);
        await refresh();
        onUpdated && onUpdated();
      } else if (r.data.job_id) {
        setStatus({ status: "running", source_file: f.name });
        pollJob(r.data.job_id);
      }
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message || "Upload failed");
      setStatus(null);
      setBusy(false);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete all ${existing.total} result rows for this championship?`)) return;
    try {
      await api.delete(`/championships/${championshipId}/results`);
      await refresh();
      onUpdated && onUpdated();
      setOk("All results cleared.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const removeRow = async (rid) => {
    try {
      await api.delete(`/results/${rid}`);
      await refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div role="dialog" aria-modal="true"
      className="fixed inset-0 z-50 bg-nkf-navy/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose} data-testid="results-import-modal">
      <div className="relative bg-white w-full max-w-4xl rounded-md shadow-xl my-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-red font-semibold">Championship Results</div>
            <div className="font-display text-xl text-nkf-blue mt-0.5 truncate">{championshipTitle}</div>
            <div className="text-xs text-slate-500 mt-1">Upload any results file — Excel, CSV, PDF, Word or image. The system auto-detects columns, parses placements, and publishes to the public championship page.</div>
          </div>
          <button onClick={onClose} data-testid="results-modal-close" className="p-2 rounded hover:bg-slate-100 text-slate-500"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Upload row */}
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="results-replace-toggle" />
                <span>Replace existing results on upload</span>
              </label>
              <div className="flex-1" />
              {existing.total > 0 && (
                <button onClick={clearAll} data-testid="results-clear-all"
                  className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-red-600 border border-red-200 bg-white px-3 py-2 rounded hover:bg-red-50">
                  <Trash2 className="h-3.5 w-3.5" /> Clear All ({existing.total})
                </button>
              )}
            </div>
            <div className="mt-3">
              <input ref={fileRef} type="file" onChange={onUpload} disabled={busy}
                accept=".xlsx,.xls,.csv,.pdf,.docx,.png,.jpg,.jpeg,.webp,.txt"
                data-testid="results-file-input"
                className="block w-full text-sm text-slate-600 file:mr-3 file:py-2.5 file:px-4 file:rounded file:border-0 file:bg-nkf-blue file:text-white file:text-xs file:uppercase file:tracking-widest file:font-bold hover:file:bg-nkf-navy disabled:opacity-50" />
              <div className="text-[11px] text-slate-500 mt-2">Supported: .xlsx .xls .csv .pdf .docx .png .jpg (max ~20 MB). Spreadsheets are parsed instantly; PDFs/images use AI and may take 30–90 seconds.</div>
            </div>

            {status && (
              <div className="mt-4 p-3 rounded bg-white border border-slate-200" data-testid="results-import-status">
                <div className="flex items-center gap-2 text-sm">
                  {status.status === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    : status.status === "failed" ? <AlertCircle className="h-4 w-4 text-red-600" />
                    : <Loader2 className="h-4 w-4 text-nkf-blue animate-spin" />}
                  <span className="font-semibold text-slate-700">
                    {status.status === "done" ? "Done" :
                     status.status === "failed" ? "Failed" :
                     status.status === "uploading" ? "Uploading…" :
                     status.status === "running" ? "AI is reading the file…" : status.status}
                  </span>
                  <span className="text-slate-500 truncate">{status.source_file}</span>
                </div>
                {status.status === "done" && (
                  <div className="text-xs text-slate-600 mt-1">Parsed {status.parsed} rows · Inserted {status.inserted}.</div>
                )}
                {status.status === "failed" && (
                  <div className="text-xs text-red-600 mt-1">{status.error}</div>
                )}
                {status.status === "running" && (
                  <div className="text-xs text-slate-500 mt-1">You can leave this modal open; it will update automatically.</div>
                )}
              </div>
            )}

            {err && <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-2.5 rounded text-sm"><AlertCircle className="h-4 w-4 mt-0.5" /> {err}</div>}
            {ok && <div className="mt-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 p-2.5 rounded text-sm"><CheckCircle2 className="h-4 w-4 mt-0.5" /> {ok}</div>}
          </div>

          {/* Preview of existing */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="h-4 w-4 text-nkf-red" />
              <h3 className="font-display text-lg text-nkf-blue">Currently published</h3>
              <span className="text-xs uppercase tracking-widest text-slate-500">{existing.total} rows · {existing.grouped.length} categories</span>
            </div>
            {existing.total === 0 ? (
              <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded p-6 text-center flex flex-col items-center gap-2">
                <FileSpreadsheet className="h-6 w-6 text-slate-400" />
                No results uploaded yet. Pick a file above to publish them.
              </div>
            ) : (
              <div className="max-h-[380px] overflow-y-auto border border-slate-200 rounded">
                {existing.grouped.map((g, i) => (
                  <div key={i} className="border-b border-slate-100 last:border-b-0">
                    <div className="bg-slate-50 px-3 py-2 text-[11px] uppercase tracking-widest text-nkf-blue font-semibold flex items-center justify-between">
                      <span className="truncate">{g.category}</span>
                      <span className="text-slate-500">{g.rows.length}</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {g.rows.map((r) => (
                        <div key={r.id} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                          <PositionBadge pos={r.position} />
                          <div className="flex-1 min-w-0">
                            <div className="truncate text-slate-800">{r.athlete_name}</div>
                            {(r.district || r.academy) && <div className="text-[11px] text-slate-500 truncate">{[r.district, r.academy].filter(Boolean).join(" · ")}</div>}
                          </div>
                          <button onClick={() => removeRow(r.id)} className="text-xs text-red-500 hover:text-red-700 p-1" data-testid={`results-del-${r.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PositionBadge({ pos }) {
  if (!pos) return null;
  const p = String(pos).trim().toUpperCase();
  const map = {
    "GOLD": { c: "bg-nkf-gold/15 text-nkf-navy border-nkf-gold/40", t: "Gold" },
    "SILVER": { c: "bg-slate-200 text-slate-700 border-slate-300", t: "Silver" },
    "BRONZE": { c: "bg-amber-100 text-amber-800 border-amber-300", t: "Bronze" },
    "I": { c: "bg-nkf-gold/15 text-nkf-navy border-nkf-gold/40", t: "1" },
    "II": { c: "bg-slate-200 text-slate-700 border-slate-300", t: "2" },
    "III": { c: "bg-amber-100 text-amber-800 border-amber-300", t: "3" },
    "1": { c: "bg-nkf-gold/15 text-nkf-navy border-nkf-gold/40", t: "1" },
    "2": { c: "bg-slate-200 text-slate-700 border-slate-300", t: "2" },
    "3": { c: "bg-amber-100 text-amber-800 border-amber-300", t: "3" },
  };
  const m = map[p] || { c: "bg-slate-100 text-slate-700 border-slate-200", t: p };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold border px-1.5 py-0.5 rounded min-w-[36px] justify-center ${m.c}`}>
      <Medal className="h-3 w-3" /> {m.t}
    </span>
  );
}
